import React, { useState, useEffect } from 'react';

const EXPENSE_EMOJIS = {
  housing: ['🏠', '🏢', '🏘️', '🔑', '🏡', '🧹', '🧾', '🔨', '🪑', '📝', '🔧', '🏗️', '🌳', '🔒'],
  // house, apartment, neighborhood, keys, home, cleaning, receipt/bill, repairs, furniture, lease, maintenance, construction, landscaping, security
  
  food: ['🍽️', '🛒', '☕', '🍳', '🍕', '🍔', '🥤', '🍦', '🍷', '🌮', '🍲', '🥪', '🍛', '🥗'],
  // dining out, groceries, coffee, cooking, pizza, burgers, drinks, desserts, alcohol, fast food, meal prep, sandwiches, takeout, salad
  
  transportation: ['🚗', '⛽', '🚌', '🚇',  '🚕', '✈️', '📜', '🔧', '🚲', '🚂', '🚶', '🚢', '🚏', '🅿️'],
  // car, bus, subway, gas, taxi, plane, car insurance, car maintenance, bicycle, train, walking, boat, bus stop, parking
  
  utilities: ['💡', '📱', '💧', '🔥', '📺', '🌡️', '🌐', '💻', '🗑️', '🧻', '📡', '☎️', '🧯', '⚡'],
  // electricity, mobile, water, heating, tv, cooling, internet, computer, trash, toilet paper/supplies, satellite, landline, fire safety, power
  
  entertainment: ['🎬', '🎮', '🎵', '🎨', '🎫', '📱', '📖', '🎤', '⚽', '🎲', '🍿', '🎯', '🏊', '🎪'],
  // movies, gaming, music, art, tickets, mobile games, books, concerts, sports, board games, popcorn/snacks, hobbies, swimming, events
  
  education: ['📚', '🎓', '✏️', '💻', '🏫', '📝', '🧪', '🌍', '🔬', '🎒', '🖨️', '🧮', '📐', '🎭'],
  // books, graduation, supplies, online learning, school, notes, lab fees, field trips, research, backpack, printing, calculator, tools, extracurriculars
  
  healthcare: ['💊', '🏥', '🦷', '🧘', '👁️', '💆', '🧠', '🦮', '💉', '🏋️', '🧬', '👓', '🦿', '🧴'],
  // medication, hospital, dental, wellness, vision, massage, mental health, support services, vaccination, gym, preventative care, glasses, mobility, skincare
  
  debt_payment: ['💰', '💳', '🏦', '📊', '💵', '📑', '🏠', '🚗', '🎓', '📱', '💹', '⏰', '🔄', '🧮'],
  // money, credit card, bank, financial plan, cash, documents, mortgage, auto loan, student loan, phone payment, interest, payment due, refinance, calculations
  
  personal: ['🛍️', '👕', '💄', '💅', '✂️', '👶', '🐶', '🎁', '💍', '👔', '👟', '🧦', '🧢', '👝'],
  // shopping, clothing, makeup, nails, haircut, baby supplies, pets, gifts, jewelry, formal wear, shoes, socks, hats, bags
};

const BUDGET_CATEGORY_TO_EMOJI_MAP = {
  'Housing': 'housing',
  'Food': 'food',
  'Transportation': 'transportation',
  'Utilities': 'utilities',
  'Entertainment': 'entertainment',
  'Education': 'education',
  'Healthcare': 'healthcare',
  'Debt Payment': 'debt_payment',
  'Personal': 'personal'
};

const ImprovedEmojiPicker = ({ value, onChange, budgetCategory }) => {
  const [relevantCategories, setRelevantCategories] = useState({});
  
  useEffect(() => {
    if (budgetCategory) {
      const categoryKey = BUDGET_CATEGORY_TO_EMOJI_MAP[budgetCategory];
      if (categoryKey) {
        setRelevantCategories({
          [categoryKey]: EXPENSE_EMOJIS[categoryKey]
        });
      } else {
        // If no direct match, show all emojis grouped by category
        setRelevantCategories(EXPENSE_EMOJIS);
      }
    } else {
      // If no category, show default common emojis
      setRelevantCategories({
        'common': ['💸', '💰', '💳', '💵', '🧾', '📑', '📊', '📝']
      });
    }
  }, [budgetCategory]);

  const renderEmojiCategory = (category, emojis) => (
    <div key={category} style={styles.categoryContainer}>
      <div style={styles.categoryTitle}>
        {category === 'common' ? 'Common' : category.charAt(0).toUpperCase() + category.slice(1)}
      </div>
      <div style={styles.scrollContainer}>
        {emojis.map(emoji => (
          <button
            key={emoji}
            style={{
              ...styles.emojiButton,
              ...(value === emoji ? styles.selectedEmoji : {})
            }}
            onClick={() => onChange(emoji)}
            type="button"
          >
            <span style={styles.emojiText}>{emoji}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    if (Object.keys(relevantCategories).length === 0) {
      return (
        <div style={styles.placeholderContainer}>
          <span style={styles.placeholderText}>Select category</span>
          <span style={styles.placeholderEmoji}>📋</span>
        </div>
      );
    }

    return (
      <div style={styles.contentContainer}>
        {Object.entries(relevantCategories).map(([category, emojis]) => 
          renderEmojiCategory(category, emojis)
        )}
      </div>
    );
  };

  return (
    <div style={styles.pickerContainer}>
      {renderContent()}
    </div>
  );
};

const styles = {
  pickerContainer: {
    width: '100%',
  },
  contentContainer: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '8px',
  },
  categoryContainer: {
    marginBottom: '16px',
  },
  categoryTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#666',
  },
  scrollContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '8px',
    paddingVertical: '8px',
    paddingHorizontal: '4px',
  },
  emojiButton: {
    width: '40px',
    height: '40px',
    borderRadius: '20px',
    border: '1px solid #ddd',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '4px',
    cursor: 'pointer',
    backgroundColor: '#fff',
  },
  selectedEmoji: {
    borderColor: '#007AFF',
    backgroundColor: '#E3F2FF',
  },
  emojiText: {
    fontSize: '20px',
  },
  placeholderContainer: {
    padding: '8px',
    borderRadius: '8px',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px dashed #ddd',
    flexDirection: 'row',
    height: '80px',
  },
  placeholderText: {
    fontSize: '14px',
    color: '#666',
    marginRight: '4px',
    textAlign: 'center',
  },
  placeholderEmoji: {
    fontSize: '16px',
  }
};

export default ImprovedEmojiPicker;